import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { environment } from 'src/environments/environment';
import { TimezoneService } from 'src/app/shared/utilities/timezone.service';
import { DeviceService } from '../../../services/device.service';

export interface ChartDataViewData {
  deviceData: any;
  chartData: {
    xAxisData: string[];
    seriesData: {
      name: string;
      data: number[];
      color: string;
      unit?: string;
    }[];
  };
  timeRange: string;
  gatewayDeviceData?: any[];
  isGatewayView?: boolean;
  userTimezoneUtcName?: string;
  // Context passed from the parent chart component so the dialog can re-fetch
  // data when the user changes the from/to date range.
  selectedRange?: 'day' | 'week' | 'month' | 'year';
  selectedDateRange?: { start: Date; end: Date };
  // Captured by the parent at open time so refetches always have a stable,
  // correct device type name independent of later deviceData mutations.
  deviceTypeName?: string;
}

@Component({
  selector: 'app-device-chart-data-view',
  templateUrl: './device-chart-data-view.component.html',
  styleUrls: ['./device-chart-data-view.component.scss']
})
export class DeviceChartDataViewComponent implements OnInit {
  @ViewChild('agGrid') agGrid!: AgGridAngular;

  public environment = environment;
  gridApi!: GridApi;
  columnDefs: ColDef[] = [];
  rowData: any[] = [];

  defaultColDef: ColDef = {
    sortable: true,
    resizable: true,
    minWidth: 150,
    filter: true,
    floatingFilter: true,
  };

  gridOptions: GridOptions = {
    pagination: true,
    paginationPageSize: environment.paginationPageSize,
    paginationPageSizeSelector: environment.paginationPageSizeSelector,
    suppressPaginationPanel: false,
    domLayout: 'normal'
  };

  // Date range state used by the from/to pickers in the dialog header.
  selectedDateRange: { start: Date; end: Date } = { start: new Date(), end: new Date() };
  // Granularity used by the Day/Week/Month/Year toggle in the dialog header.
  selectedRange: 'day' | 'week' | 'month' | 'year' = 'day';
  isLoading = false;

  // Captured once on init so refetches (previous/next/range change) always send
  // a stable deviceTypeName, even if the shared deviceData reference is mutated
  // elsewhere (e.g. the parent chart) and loses the value.
  private deviceTypeName = '';

  constructor(
    public dialogRef: MatDialogRef<DeviceChartDataViewComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ChartDataViewData,
    private timezoneService: TimezoneService,
    private deviceService: DeviceService
  ) { }

  ngOnInit(): void {
    // Initialize the granularity and date range from the context passed by the parent chart.
    this.selectedRange = (this.data.selectedRange || this.data.timeRange || 'day') as 'day' | 'week' | 'month' | 'year';
    // Capture the device type name once up front so later refetches are not
    // affected by mutations to the shared deviceData reference. Prefer the
    // value the parent captured at open time, falling back to deviceData.
    this.deviceTypeName = this.data.deviceTypeName || this.data.deviceData?.deviceTypeName || '';
    if (this.data.selectedDateRange) {
      this.selectedDateRange = {
        start: new Date(this.data.selectedDateRange.start),
        end: new Date(this.data.selectedDateRange.end)
      };
    }

    if (this.data.isGatewayView && this.data.gatewayDeviceData) {
      this.setupGatewayGrid();
    }
    else {
      this.setupGrid();
    }
  }

  /**
   * Called when the user changes the Day/Week/Month/Year granularity.
   * Recalculates the date window from the end date and re-fetches.
   */
  onRangeChange(): void {
    this.recalculateRangeFromEndDate();
    this.onDateRangeChange();
  }

  /**
   * Recalculates the start date based on the end date (anchor) and the
   * selected granularity, mirroring the parent chart component.
   */
  private recalculateRangeFromEndDate(): void {
    if (!this.selectedDateRange?.end) {
      return;
    }
    const endDate = new Date(this.selectedDateRange.end);
    let startDate = new Date(endDate);

    switch (this.selectedRange) {
      case 'week':
        startDate.setDate(endDate.getDate() - 6);
        break;
      case 'month':
        startDate.setMonth(endDate.getMonth() - 1);
        break;
      case 'year':
        startDate.setFullYear(endDate.getFullYear() - 1);
        break;
      default: // day
        startDate = new Date(endDate);
        break;
    }

    this.selectedDateRange = { start: startDate, end: endDate };
  }

  /**
   * Shift the date range one interval into the past (based on the selected
   * granularity) and re-fetch.
   */
  navigatePrevious(): void {
    this.shiftDateRange(-1);
    this.onDateRangeChange();
  }

  /**
   * Shift the date range one interval into the future and re-fetch.
   */
  navigateNext(): void {
    this.shiftDateRange(1);
    this.onDateRangeChange();
  }

  /**
   * Shifts the selected date range by the given direction (-1 previous,
   * +1 next) based on the selected granularity (day/week/month/year).
   */
  private shiftDateRange(direction: number): void {
    const start = new Date(this.selectedDateRange.start);
    const end = new Date(this.selectedDateRange.end);
    const range = this.selectedRange;

    switch (range) {
      case 'week':
        start.setDate(start.getDate() + 7 * direction);
        end.setDate(end.getDate() + 7 * direction);
        break;
      case 'month':
        start.setMonth(start.getMonth() + direction);
        end.setMonth(end.getMonth() + direction);
        break;
      case 'year':
        start.setFullYear(start.getFullYear() + direction);
        end.setFullYear(end.getFullYear() + direction);
        break;
      default: // day
        start.setDate(start.getDate() + direction);
        end.setDate(end.getDate() + direction);
        break;
    }

    this.selectedDateRange = { start, end };
  }

  /**
   * Re-fetch the table data from the API whenever the user changes the
   * from or to date. Rebuilds the grid with the latest data.
   */
  onDateRangeChange(): void {
    const deviceId = this.data.deviceData?.id;
    if (!deviceId) {
      return;
    }
    if (!this.selectedDateRange.start || !this.selectedDateRange.end) {
      return;
    }

    const { startDate, endDate } = this.getDateRangeParams();
    const view = this.selectedRange;
    const deviceTypeName = this.deviceTypeName || this.data.deviceData?.deviceTypeName || '';
    const offset = 0;
    const limit = 10000;

    this.isLoading = true;
    this.deviceService.getChartTableViewData(deviceId, startDate, endDate, offset, limit, view, deviceTypeName).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        this.data.gatewayDeviceData = response?.chartDeviceDetails || [];
        this.data.isGatewayView = true;
        this.setupGatewayGrid();
        if (this.gridApi) {
          this.gridApi.setGridOption('rowData', this.rowData);
        }
      },
      error: (error: any) => {
        this.isLoading = false;
        console.error('Error refreshing chart table view data:', error);
        this.data.gatewayDeviceData = [];
        this.setupGatewayGrid();
        if (this.gridApi) {
          this.gridApi.setGridOption('rowData', this.rowData);
        }
      }
    });
  }

  /**
   * Builds timezone-aware start/end dates matching the parent chart's logic.
   */
  private getDateRangeParams(): { startDate: Date; endDate: Date } {
    let startDate = new Date(this.selectedDateRange.start);
    let endDate = new Date(this.selectedDateRange.end);

    // Convert to the day boundaries in the user's timezone (matches parent chart).
    if (this.data.userTimezoneUtcName) {
      startDate = this.timezoneService.convertToUserTimezone(startDate, this.data.userTimezoneUtcName, 'start');
      endDate = this.timezoneService.convertToUserTimezone(endDate, this.data.userTimezoneUtcName, 'end');
    } else {
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
    }

    // Then convert to location timezone if available.
    const locationTimezoneId = this.data.deviceData?.location?.timezoneId || this.data.deviceData?.timezoneId;
    if (locationTimezoneId) {
      startDate = this.deviceService.convertToLocationTimezone(startDate, locationTimezoneId);
      endDate = this.deviceService.convertToLocationTimezone(endDate, locationTimezoneId);
    }

    return { startDate, endDate };
  }

  private createIndexColumn(): ColDef {
    return {
      headerName: '#',
      field: 'index',
      width: 70,
      minWidth: 70,
      maxWidth: 70,
      cellClass: 'text-center',
      sortable: false,
      filter: false,
      floatingFilter: false,
      pinned: 'left'
    };
  }

  private setupGrid(): void {
    // Define fixed columns
    const fixedColumns: ColDef[] = [
      this.createIndexColumn(),
      {
        headerName: 'Time Period',
        field: 'timePeriod',
        width: 150,
        minWidth: 120,
      },
      {
        headerName: 'Parent Device Number',
        field: 'parentDeviceNumber',
        width: 180,
        minWidth: 150,
        valueGetter: () => this.data.deviceData?.parentDeviceNumber || 'N/A'
      },
      {
        headerName: 'Name',
        field: 'name',
        width: 200,
        minWidth: 150,
        valueGetter: () => this.data.deviceData?.name || 'N/A'
      },
      {
        headerName: 'Device Number',
        field: 'deviceNumber',
        width: 150,
        minWidth: 120,
        valueGetter: () => this.data.deviceData?.deviceNumber || 'N/A'
      },
      {
        headerName: 'Serial Number',
        field: 'serialNumber',
        width: 150,
        minWidth: 120,
        valueGetter: () => this.data.deviceData?.serialNumber || 'N/A'
      },
      {
        headerName: 'Device Type',
        field: 'deviceType',
        width: 150,
        minWidth: 120,
        valueGetter: () => this.data.deviceData?.deviceTypeName || 'N/A'
      },
      {
        headerName: 'Building',
        field: 'building',
        width: 150,
        minWidth: 120,
        valueGetter: () => this.data.deviceData?.buildingName || 'N/A'
      },
      {
        headerName: 'Floor',
        field: 'floor',
        width: 120,
        minWidth: 100,
        valueGetter: () => this.data.deviceData?.floorName || 'N/A'
      },
      {
        headerName: 'Department',
        field: 'department',
        width: 150,
        minWidth: 120,
        valueGetter: () => this.data.deviceData?.departmentName || 'N/A'
      },
      {
        headerName: 'Customer',
        field: 'customer',
        width: 150,
        minWidth: 120,
        valueGetter: () => this.data.deviceData?.customerName || 'N/A'
      },
      {
        headerName: 'Location',
        field: 'location',
        width: 150,
        minWidth: 120,
        valueGetter: () => this.data.deviceData?.locationName || 'N/A'
      }
    ];

    // Create dynamic columns from chart series
    const dynamicColumns: ColDef[] = this.createDynamicColumnsFromSeries(
      this.data.chartData.seriesData,
      (series, params) => {
        if (params.value != null) {
          return `${params.value.toFixed(1)}${series.unit ? ' ' + series.unit : ''}`;
        }
        return '';
      }
    );

    // Combine all columns
    this.columnDefs = [...fixedColumns, ...dynamicColumns];

    // Prepare row data
    this.rowData = this.data.chartData.xAxisData.map((time, index) => {
      const row: any = {
        index: index + 1,
        timePeriod: time
      };

      // Add dynamic data from series
      this.data.chartData.seriesData.forEach(series => {
        const fieldName = series.name.toLowerCase().replace(/\s+/g, '_');
        row[fieldName] = series.data[index];
      });

      return row;
    });
  }

  private setupGatewayGrid(): void {
    if (!this.data.gatewayDeviceData || this.data.gatewayDeviceData.length === 0) {
      // Clear the grid so stale rows from a previous range are not shown.
      this.rowData = [];
      return;
    }

    // Define fixed columns for gateway device data
    const fixedColumns: ColDef[] = [
      this.createIndexColumn(),
      {
        headerName: 'Device Number',
        field: 'deviceNumber',
        width: 150,
        minWidth: 120,
      },
      {
        headerName: 'Timestamp',
        field: 'timestamp',
        width: 220,
        minWidth: 200,
        valueFormatter: (params: any) => {
          if (params.value) {
            const date = new Date(params.value);
            // Use user's timezone if available, otherwise use browser local timezone
            if (this.data.userTimezoneUtcName) {
              return this.timezoneService.formatDateLongInTimezone(date, this.data.userTimezoneUtcName);
            }
            return date.toLocaleString();
          }
          return '';
        }
      },
      {
        headerName: 'Server Timestamp',
        field: 'server_time_stamp',
        width: 220,
        minWidth: 200,
        valueFormatter: (params: any) => {
          if (params.value) {
            const date = new Date(params.value);
            // Use user's timezone if available, otherwise use browser local timezone
            if (this.data.userTimezoneUtcName) {
              return this.timezoneService.formatDateLongInTimezone(date, this.data.userTimezoneUtcName);
            }
            return date.toLocaleString();
          }
          return '';
        }
      }
    ];

    // Extract dynamic columns from the data attributes
    const dynamicColumns: ColDef[] = this.createDynamicColumnsFromGatewayDevice(this.data.gatewayDeviceData);

    // Combine all columns
    this.columnDefs = [...fixedColumns, ...dynamicColumns];

    // Prepare row data
    this.rowData = this.data.gatewayDeviceData.map((item, index) => {
      const row: any = {
        index: index + 1,
        deviceNumber: item.deviceNumber,
        timestamp: item.timestamp,
        server_time_stamp: item.server_time_stamp
      };

      // Add dynamic data from the data object
      if (item.data) {
        Object.keys(item.data).forEach(key => {
          row[`data_${key}`] = item.data[key];
        });
      }

      return row;
    });
  }

  private createDynamicColumnsFromSeries(
    seriesData: any[],
    valueFormatter: (series: any, params: any) => string
  ): ColDef[] {
    return seriesData.map(series => ({
      headerName: series.name,
      field: series.name.toLowerCase().replace(/\s+/g, '_'),
      width: 150,
      minWidth: 120,
      cellStyle: { color: series.color, fontWeight: '600' },
      valueFormatter: (params: any) => valueFormatter(series, params)
    }));
  }

  private createDynamicColumnsFromGatewayDevice(eGaugeData: any[]): ColDef[] {
    const dynamicColumns: ColDef[] = [];
    const colors = ['#7cb5ec', '#434348', '#90ed7d', '#f7a35c', '#8085e9', '#f15c80'];

    // Collect ALL unique keys from ALL rows' data objects
    const allDataKeys = new Set<string>();
    eGaugeData.forEach(item => {
      if (item.data) {
        Object.keys(item.data).forEach(key => allDataKeys.add(key));
      }
    });

    const dataKeys = Array.from(allDataKeys);
    dataKeys.forEach((key, index) => {
      const seriesColor = colors[index % colors.length];

        dynamicColumns.push({
          headerName: key,
          field: `data_${key}`,
          width: 200,
          minWidth: 160,
          headerTooltip: key,
          wrapHeaderText: true,
          autoHeaderHeight: true,
          cellStyle: { color: seriesColor, fontWeight: '600' },
          valueFormatter: (params: any) => {
            const value = params.value;

            if (value == null) return 'N/A';

            // Number handling
            if (typeof value === 'number') {
              if (Number.isInteger(value)) {
                return `${value}`;
              } else {
                return `${value.toFixed(2)}`;
              }
            }

            // String handling
            if (typeof value === 'string') {
              const numericValue = Number(value);

              // If string is numeric
              if (!isNaN(numericValue)) {
                if (Number.isInteger(numericValue)) {
                  return `${numericValue}`;
                } else {
                  return `${numericValue.toFixed(2)}`;
                }
              }

              // Non-numeric string
              return value;
            }

            // Fallback
            return 'N/A';
          }
        });
      });

    return dynamicColumns;
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;

    // Auto-size columns on initial load
    setTimeout(() => {
      if (this.gridApi) {
        this.gridApi.sizeColumnsToFit();
      }
    }, 100);
  }

  downloadCSV(): void {
    if (this.gridApi) {
      const params = {
        fileName: `${this.data.deviceData?.name || 'device'}-data-${new Date().toISOString().slice(0, 10)}.csv`,
        columnSeparator: ','
      };
      this.gridApi.exportDataAsCsv(params);
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
